import type { DimensionSide, DimensionType } from "@/core/export-image"
import { fillToSize, fitToSize } from "@/core/utils/resize-image"

const FALLBACK_EXPORT_DIMENSIONS = [6000, 4000] as const
const MEGABYTE = 1_000_000
const GIGABYTE = 1_000_000_000

// Estimate-only caps keep bad metadata from making the dialog allocate huge TIFF
// strip arrays. Actual export dimensions remain uncapped.
export const ESTIMATED_EXPORT_MAX_DIMENSION = 65_535
export const ESTIMATED_EXPORT_MAX_PIXELS = 500_000_000

const isPositiveFiniteDimension = (dimension: number | null | undefined): dimension is number =>
	typeof dimension === "number" && Number.isFinite(dimension) && dimension > 0

const scaleEstimatedExportSize = (size: [number, number], scale: number): [number, number] => [
	Math.max(1, Math.floor(size[0] * scale)),
	Math.max(1, Math.floor(size[1] * scale)),
]

const capEstimatedExportSize = (size: [number, number]): [number, number] => {
	const dimensionScale = Math.min(
		1,
		ESTIMATED_EXPORT_MAX_DIMENSION / size[0],
		ESTIMATED_EXPORT_MAX_DIMENSION / size[1],
	)
	const dimensionCappedSize =
		dimensionScale < 1 ? scaleEstimatedExportSize(size, dimensionScale) : size
	const pixelCount = dimensionCappedSize[0] * dimensionCappedSize[1]

	if (pixelCount <= ESTIMATED_EXPORT_MAX_PIXELS) {
		return dimensionCappedSize
	}

	return scaleEstimatedExportSize(
		dimensionCappedSize,
		Math.sqrt(ESTIMATED_EXPORT_MAX_PIXELS / pixelCount),
	)
}

const normalizeEstimatedExportSize = (size: [number, number]): [number, number] =>
	capEstimatedExportSize([Math.max(1, Math.round(size[0])), Math.max(1, Math.round(size[1]))])

export const getEstimatedExportDimensions = (
	width: number | null | undefined,
	height: number | null | undefined,
	dimensionType: DimensionType,
	dimensionSide: DimensionSide,
	dimension: number,
) => {
	const sourceSize: [number, number] =
		isPositiveFiniteDimension(width) && isPositiveFiniteDimension(height)
			? [width, height]
			: [...FALLBACK_EXPORT_DIMENSIONS]

	if (dimensionType !== "Custom") {
		return normalizeEstimatedExportSize(sourceSize)
	}

	const targetDimension = Math.max(1, dimension)
	const targetSize: [number, number] = [targetDimension, targetDimension]

	return normalizeEstimatedExportSize(
		dimensionSide === "Long side"
			? fitToSize(sourceSize, targetSize)
			: fillToSize(sourceSize, targetSize),
	)
}

export const formatEstimatedExportSize = (bytes: number) => {
	if (bytes < GIGABYTE) {
		const megabytes = bytes / MEGABYTE

		if (megabytes < 1 && bytes > 0) {
			return "<1 MB"
		}

		return `${megabytes.toFixed(megabytes < 100 ? 1 : 0)} MB`
	}

	const gigabytes = bytes / GIGABYTE

	return `${gigabytes.toFixed(gigabytes < 10 ? 2 : 1)} GB`
}
